import React, { useState, useMemo, useRef } from 'react';
import { Tag, Button, Space, Typography, message, Popconfirm, Spin } from 'antd';
import { CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';
import { generateBulkEvaluationDraft, fetchSubmissionPreview } from '../services/bulkEvaluationService';
import { saveTrainerEval, gradeManualModule } from '../services/moodleService';
import { deriveBulkEvaluationRows, formatDisplayName } from '../utils/moduleUtils';
import { BulkEvaluationRow } from '../types/training';
import { AiEvaluationSummary, QuestionResult } from './AiDraftReview';

const { Text } = Typography;

type RowState = 'no-submission' | 'eligible' | 'generating' | 'ready' | 'saved' | 'done' | 'blocked';

// Color language matches the status tags already used elsewhere in this
// feature (blue "Eligible," green "Done") - lets a trainer read a whole
// batch's progress from the header colors alone, without expanding rows.
const ROW_STATE_STYLE: Record<RowState, { background: string; border: string }> = {
    'no-submission': { background: '#fafafa', border: '#f0f0f0' },
    eligible: { background: '#ffffff', border: '#f0f0f0' },
    generating: { background: '#fffbe6', border: '#ffe58f' },
    ready: { background: '#f0f5ff', border: '#adc6ff' },
    saved: { background: '#f6ffed', border: '#b7eb8f' },
    done: { background: '#f6ffed', border: '#b7eb8f' },
    blocked: { background: '#fff1f0', border: '#ffa39e' },
};

interface RowDraft {
    score?: number;
    evaluation?: string;
    confidence?: 'high' | 'medium' | 'low';
    submissionText?: string;
    questionResults?: QuestionResult[];
    possiblyAiWritten?: { reason: string };
    blockedReason?: string;
    generating?: boolean;
    saving?: boolean;
    saved?: boolean;
    previewLoading?: boolean;
}

interface BulkEvaluationBoardProps {
    courseId: number | string;
    assignmentId: number;
    maxCredits: number;
    courseProgress: any;
    onSaved: () => void;
}

const BulkEvaluationBoard: React.FC<BulkEvaluationBoardProps> = ({
    courseId, assignmentId, maxCredits, courseProgress, onSaved,
}) => {
    const rows = useMemo(
        () => deriveBulkEvaluationRows(courseProgress, assignmentId),
        [courseProgress, assignmentId]
    );
    const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
    const [reEvaluate, setReEvaluate] = useState<Set<number>>(new Set());
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [showSubmission, setShowSubmission] = useState<Record<number, boolean>>({});
    const [generatingAll, setGeneratingAll] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const cancelRequestedRef = useRef(false);

    const isEligible = (row: BulkEvaluationRow) => row.status === 'eligible' || reEvaluate.has(row.userid);

    const generateRow = async (row: BulkEvaluationRow) => {
        setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], generating: true, blockedReason: undefined } }));
        const result = await generateBulkEvaluationDraft(
            courseId, { userid: row.userid, email: row.email }, assignmentId, maxCredits
        );
        if (result.status === 'ok' && result.draft) {
            setShowSubmission(prev => ({ ...prev, [row.userid]: prev[row.userid] ?? true }));
            setDrafts(prev => ({
                ...prev,
                [row.userid]: {
                    score: result.draft!.score,
                    evaluation: result.draft!.evaluation,
                    confidence: result.draft!.confidence,
                    submissionText: result.draft!.submissionText,
                    questionResults: result.draft!.questionResults,
                    possiblyAiWritten: result.draft!.possiblyAiWritten,
                    generating: false,
                },
            }));
        } else {
            setDrafts(prev => ({
                ...prev,
                [row.userid]: { ...prev[row.userid], generating: false, blockedReason: result.blockedReason || 'Could not generate a draft.' },
            }));
        }
    };

    // Lets a trainer read a submission (and know why it's blocked, if it is)
    // by simply expanding a row - never calls the AI by itself.
    const loadPreview = async (row: BulkEvaluationRow) => {
        setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], previewLoading: true } }));
        const result = await fetchSubmissionPreview(courseId, { userid: row.userid, email: row.email }, assignmentId);
        setShowSubmission(prev => ({ ...prev, [row.userid]: prev[row.userid] ?? true }));
        setDrafts(prev => ({
            ...prev,
            [row.userid]: {
                ...prev[row.userid],
                previewLoading: false,
                submissionText: result.submissionText ?? prev[row.userid]?.submissionText,
                blockedReason: result.status === 'blocked' ? result.blockedReason : prev[row.userid]?.blockedReason,
            },
        }));
    };

    // Sequential - one AI call at a time, never Promise.all - matches the
    // spec's explicit "one call per participant, never in parallel" decision.
    const generateAllPending = async () => {
        const targets = rows.filter(r => isEligible(r) && drafts[r.userid]?.evaluation == null);
        cancelRequestedRef.current = false;
        setGeneratingAll(true);
        setProgress({ done: 0, total: targets.length });
        for (let i = 0; i < targets.length; i++) {
            if (cancelRequestedRef.current) break;
            await generateRow(targets[i]);
            setProgress({ done: i + 1, total: targets.length });
        }
        setGeneratingAll(false);
        setProgress(null);
    };

    const requestCancel = () => {
        cancelRequestedRef.current = true;
    };

    const saveRow = async (row: BulkEvaluationRow) => {
        const draft = drafts[row.userid];
        if (!draft || draft.evaluation == null) return;
        setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], saving: true } }));
        try {
            await saveTrainerEval(assignmentId, row.userid, draft.evaluation);
            if (draft.score != null) {
                await gradeManualModule(assignmentId, row.userid, draft.score);
            }
            setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], saving: false, saved: true } }));
            onSaved();
        } catch {
            message.error(`Failed to save evaluation for ${row.fullname}`);
            setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], saving: false } }));
        }
    };

    const saveAllReviewed = async () => {
        const targets = rows.filter(r => drafts[r.userid]?.evaluation != null && !drafts[r.userid]?.saved);
        for (const row of targets) {
            await saveRow(row);
        }
    };

    const pendingCount = rows.filter(r => isEligible(r) && drafts[r.userid]?.evaluation == null).length;
    const reviewedCount = rows.filter(r => drafts[r.userid]?.evaluation != null && !drafts[r.userid]?.saved).length;

    const getRowState = (row: BulkEvaluationRow): RowState => {
        const draft = drafts[row.userid];
        if (row.status === 'no-submission') return 'no-submission';
        if (draft?.generating) return 'generating';
        if (draft?.blockedReason) return 'blocked';
        if (draft?.saved) return 'saved';
        if (draft?.evaluation != null) return 'ready';
        if (row.status === 'done' && !reEvaluate.has(row.userid)) return 'done';
        return 'eligible';
    };

    const ROW_STATE_TAG: Record<RowState, { color?: string; label: string } | null> = {
        'no-submission': { label: 'No Submission' },
        eligible: { color: 'blue', label: 'Eligible' },
        generating: { color: 'gold', label: 'Generating' },
        ready: { color: 'blue', label: 'Ready to review' },
        saved: { color: 'green', label: 'Saved' },
        done: { color: 'green', label: 'Done' },
        blocked: { color: 'red', label: 'Blocked' },
    };

    const renderHeader = (row: BulkEvaluationRow, expanded: boolean, canExpand: boolean) => {
        const draft = drafts[row.userid];
        const done = row.status === 'done' && !reEvaluate.has(row.userid);
        const state = getRowState(row);
        const tag = ROW_STATE_TAG[state];
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div
                    role="button"
                    tabIndex={canExpand ? 0 : -1}
                    aria-expanded={expanded}
                    aria-disabled={!canExpand}
                    onClick={() => {
                        if (!canExpand) return;
                        const alreadyExpanded = expandedRows.has(row.userid);
                        setExpandedRows(prev => {
                            const next = new Set(prev);
                            if (next.has(row.userid)) next.delete(row.userid); else next.add(row.userid);
                            return next;
                        });
                        const d = drafts[row.userid];
                        const hasData = d?.submissionText != null || d?.evaluation != null || d?.blockedReason != null;
                        if (!alreadyExpanded && !hasData && !d?.previewLoading && !d?.generating) {
                            loadPreview(row);
                        }
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canExpand ? 'pointer' : 'default', flex: 1 }}
                >
                    {canExpand ? (expanded ? <CaretDownOutlined /> : <CaretRightOutlined />) : <span style={{ width: 12 }} />}
                    <div>
                        <Text strong>{formatDisplayName(row.fullname, row.email)}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>{row.email}</Text>
                    </div>
                </div>
                <Space>
                    {tag && <Tag color={tag.color}>{tag.label}</Tag>}
                    {done ? (
                        <Button size="small" onClick={() => setReEvaluate(prev => new Set(prev).add(row.userid))}>
                            Re-evaluate
                        </Button>
                    ) : row.status !== 'no-submission' && (
                        <>
                            <Button size="small" loading={draft?.generating} onClick={() => generateRow(row)}>
                                {draft?.evaluation != null ? 'Regenerate' : 'Generate'}
                            </Button>
                            {draft?.evaluation != null && (
                                <Button
                                    size="small" type="primary"
                                    loading={draft?.saving} disabled={!!draft?.saved}
                                    onClick={() => saveRow(row)}
                                >
                                    {draft?.saved ? 'Saved' : 'Save'}
                                </Button>
                            )}
                        </>
                    )}
                </Space>
            </div>
        );
    };

    const renderBody = (row: BulkEvaluationRow) => {
        const draft = drafts[row.userid];
        if (!draft || draft.previewLoading) return <Spin size="small" />;
        if (draft.blockedReason && draft.submissionText == null) return <Text type="danger">{draft.blockedReason}</Text>;
        if (draft.submissionText == null && draft.evaluation == null) return null;

        const hasEvaluation = draft.evaluation != null;

        return (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 40%', minWidth: 0 }}>
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 13 }}>Submission</Text>
                        <Button
                            type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                            onClick={() => setShowSubmission(prev => ({ ...prev, [row.userid]: !prev[row.userid] }))}
                        >
                            {showSubmission[row.userid] ? 'Hide' : 'Show'}
                        </Button>
                    </div>
                    {showSubmission[row.userid] && (
                        <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#595959' }}>
                            {draft.submissionText || <span style={{ color: '#bfbfbf' }}>No submission text.</span>}
                        </Text>
                    )}
                </div>
            </div>
            <div style={{ flex: '1 1 60%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!hasEvaluation && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Click Generate to draft an AI evaluation for this submission.
                    </Text>
                )}
                {hasEvaluation && draft.evaluation != null && (
                    <AiEvaluationSummary
                        evaluation={draft.evaluation}
                        onEvaluationChange={text => setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], evaluation: text } }))}
                        overallConfidence={draft.confidence}
                        questionResults={draft.questionResults}
                        possiblyAiWritten={draft.possiblyAiWritten}
                        score={draft.score ?? 0}
                        maxCredits={maxCredits}
                        onScoreChange={val => setDrafts(prev => ({ ...prev, [row.userid]: { ...prev[row.userid], score: val } }))}
                    />
                )}
            </div>
            </div>
        );
    };

    return (
        <div>
            <Space style={{ marginBottom: 16 }} wrap>
                <Button type="primary" onClick={generateAllPending} loading={generatingAll} disabled={pendingCount === 0}>
                    Generate All Pending ({pendingCount})
                </Button>
                <Button onClick={saveAllReviewed} disabled={reviewedCount === 0}>
                    Save All Reviewed ({reviewedCount})
                </Button>
                {progress && (
                    <>
                        <Text type="secondary">Evaluating {progress.done} of {progress.total}...</Text>
                        <Popconfirm
                            title="Stop generating?"
                            description="Drafts already produced are kept; remaining rows won't be evaluated."
                            okText="Stop"
                            cancelText="Keep going"
                            onConfirm={requestCancel}
                        >
                            <Button size="small" danger>Cancel</Button>
                        </Popconfirm>
                    </>
                )}
            </Space>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map(row => {
                    const canExpand = row.status !== 'no-submission';
                    const expanded = canExpand && expandedRows.has(row.userid);
                    const state = getRowState(row);
                    const colors = ROW_STATE_STYLE[state];
                    return (
                        <div
                            key={row.userid}
                            className="bulk-eval-row"
                            data-row-state={state}
                            style={{ border: `1px solid ${colors.border}`, background: colors.background, borderRadius: 8, padding: '10px 14px' }}
                        >
                            {renderHeader(row, expanded, canExpand)}
                            {expanded && <div style={{ marginTop: 12 }}>{renderBody(row)}</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BulkEvaluationBoard;
